import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import ContractorSwitcher from "./ContractorSwitcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import axios from "axios";
import TermsGate from "../pages/TermsGate";
import IntroVideoGate from "../pages/IntroVideoGate";
import {
  LayoutDashboard,
  Users,
  Phone,
  FileText,
  DollarSign,
  BarChart3,
  Clock,
  Menu,
  X,
  LogOut,
  ChevronDown,
  User,
  Target,
  ArrowLeftRight,
  Key,
  Link2,
  List,
  Settings,
  ClipboardList,
  Inbox,
  MessageSquare,
  HelpCircle,
  Smartphone,
  BookOpen,
  Bot,
  Video,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const { user, logout, impersonating, stopImpersonation, fetchUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    toast.success("Logged out successfully");
    navigate("/login");
  };

  const handleStopImpersonation = async () => {
    try {
      const userData = await stopImpersonation();
      toast.success(`Switched back to ${userData.name}`);
      navigate(`/${userData.role}`);
    } catch (error) {
      toast.error("Failed to stop impersonation");
    }
  };

  const handlePasswordChange = async () => {
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      toast.error("Please fill in all fields");
      return;
    }
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error("New passwords do not match");
      return;
    }
    
    if (passwordData.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.put(`${API}/users/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password,
      });
      toast.success("Password changed successfully!");
      setPasswordModalOpen(false);
      setPasswordData({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to change password";
      toast.error(message);
    } finally {
      setChangingPassword(false);
    }
  };

  const adminNavItems = [
    { path: "/admin", icon: LayoutDashboard, label: "Dashboard" },
    { path: "/admin/users", icon: Users, label: "User Management" },
    { path: "/admin/call-logs", icon: Phone, label: "Call Logs" },
    { path: "/admin/customer-mappings", icon: Link2, label: "Customer Mappings" },
    { path: "/admin/result-options", icon: List, label: "Disposition Values" },
    { path: "/admin/change-requests", icon: ClipboardList, label: "Change Requests" },
    { path: "/admin/approvals", icon: Inbox, label: "Approvals" },
    { path: "/admin/chat", icon: MessageSquare, label: "Support Chat" },
    { path: "/admin/chat/settings", icon: Bot, label: "Chat Templates" },
    { path: "/admin/sms", icon: Smartphone, label: "Text Messages" },
    { path: "/admin/faq", icon: HelpCircle, label: "FAQ" },
    { path: "/admin/settings", icon: Settings, label: "Settings" },
  ];

  const callerNavItems = [
    { path: "/caller", icon: Phone, label: "Call Entry" },
  ];

  const customerNavItems = [
    { path: "/customer", icon: FileText, label: "My Calls" },
    { path: "/customer/change-requests", icon: ClipboardList, label: "Change Requests" },
    { path: "/customer/chat", icon: MessageSquare, label: "Support" },
    { path: "/customer/faq", icon: HelpCircle, label: "FAQ" },
    { path: "/customer/how-it-works", icon: BookOpen, label: "How it Works" },
    { modal: "video", icon: Video, label: "Example Call Recording" },
  ];

  const getNavItems = () => {
    switch (user?.role) {
      case "admin":
        return adminNavItems;
      case "caller":
        return callerNavItems;
      case "customer":
        return customerNavItems;
      default:
        return [];
    }
  };

  const navItems = getNavItems();

  return (
    <div className="min-h-screen bg-[#f4cc1e] flex w-full max-w-full overflow-x-hidden">
      {/* Impersonation Banner */}
      {impersonating && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-[#9a3406] text-white py-2 px-4 flex items-center justify-center gap-4">
          <ArrowLeftRight size={16} />
          <span className="font-medium">
            You are viewing as {user?.name} ({user?.role})
          </span>
          <ContractorSwitcher variant="banner" />
          <Button
            size="sm"
            variant="secondary"
            onClick={handleStopImpersonation}
            data-testid="stop-impersonation-btn"
            className="h-7"
          >
            Switch Back
          </Button>
        </div>
      )}

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar - Navy Blue */}
      <aside
        className={`fixed lg:static inset-y-0 left-0 z-40 bg-[#091c3c] border-r border-[#091c3c] sidebar-transition
          ${sidebarOpen ? "w-64" : "w-20"}
          ${mobileMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}
          ${impersonating ? "mt-10" : ""}
        `}
        data-testid="sidebar"
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-4 border-b border-[#1a3a6a] flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3">
              <img 
                src="/roofnuts-logo.png" 
                alt="RoofNuts" 
                className="w-14 h-14 rounded-sm object-cover flex-shrink-0"
              />
              {sidebarOpen && (
                <span className="text-lg font-bold text-white font-['Chivo'] tracking-tight">
                  RoofNuts
                </span>
              )}
            </Link>
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hidden lg:block p-2 text-gray-400 hover:text-white transition-colors"
              data-testid="toggle-sidebar"
            >
              <Menu size={20} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item) => {
              const isActive = item.path && location.pathname === item.path;
              const cls = `flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200
                    ${isActive
                      ? "bg-[#f4cc1e] text-[#091c3c] font-medium"
                      : "text-gray-400 hover:bg-[#1a3a6a] hover:text-white"
                    }
                  `;
              if (item.modal === "video") {
                return (
                  <button
                    key="example-call-recording"
                    type="button"
                    data-testid="nav-example-call-recording"
                    className={`${cls} w-full text-left`}
                    onClick={() => { setVideoOpen(true); setMobileMenuOpen(false); }}
                  >
                    <item.icon size={20} className="flex-shrink-0" />
                    {sidebarOpen && <span>{item.label}</span>}
                  </button>
                );
              }
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={cls}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <item.icon size={20} className="flex-shrink-0" />
                  {sidebarOpen && <span>{item.label}</span>}
                </Link>
              );
            })}
          </nav>

          {/* User Info */}
          <div className="p-4 border-t border-[#1a3a6a]">
            {sidebarOpen ? (
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#1a3a6a] rounded-full flex items-center justify-center">
                  <User size={20} className="text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">
                    {user?.name}
                  </p>
                  <p className="text-xs text-gray-400 capitalize">
                    {user?.role}
                  </p>
                </div>
              </div>
            ) : (
              <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center mx-auto">
                <User size={20} className="text-muted-foreground" />
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 flex flex-col min-w-0 max-w-full overflow-hidden ${impersonating ? "mt-10" : ""}`}>
        {/* Top Bar - Navy Blue */}
        <header className="h-16 bg-[#091c3c] border-b border-[#091c3c] flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 text-gray-400 hover:text-white transition-colors"
              data-testid="mobile-menu-btn"
            >
              <Menu size={24} />
            </button>
            <h1 className="text-lg font-bold text-white font-['Chivo'] tracking-tight">
              {navItems.find((item) => item.path === location.pathname)?.label || "Dashboard"}
            </h1>
          </div>

          <div className="flex items-center gap-2">
          {!impersonating && user?.role === "admin" && <ContractorSwitcher variant="header" />}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                className="flex items-center gap-2 hover:bg-[#1a3a6a]"
                data-testid="user-menu-btn"
              >
                <div className="w-8 h-8 bg-[#1a3a6a] rounded-full flex items-center justify-center">
                  <User size={16} className="text-gray-400" />
                </div>
                <span className="hidden md:block text-sm font-medium text-white">
                  {user?.name}
                </span>
                <ChevronDown size={16} className="text-gray-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-[#091c3c] border-[#1a3a6a]">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium text-white">{user?.name}</p>
                <p className="text-xs text-gray-400">{user?.email}</p>
              </div>
              <DropdownMenuSeparator className="bg-[#1a3a6a]" />
              <DropdownMenuItem
                onClick={() => setPasswordModalOpen(true)}
                className="cursor-pointer text-white hover:bg-[#1a3a6a]"
              >
                <Key size={16} className="mr-2" />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-[#1a3a6a]" />
              <DropdownMenuItem
                onClick={handleLogout}
                className="text-[#9a3406] cursor-pointer hover:bg-[#1a3a6a]"
                data-testid="logout-btn"
              >
                <LogOut size={16} className="mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        {/* Page Content — customers must accept the Terms & Conditions before anything else */}
        <main className="flex-1 p-2 sm:p-4 lg:p-6 overflow-x-hidden overflow-y-auto w-full max-w-full">
          <div className="w-full max-w-full">
            {user?.role === "customer" && user?.intro_video_required && !user?.intro_video_completed ? (
              <IntroVideoGate onDone={fetchUser} />
            ) : user?.role === "customer" && user?.terms_accepted !== true ? (
              <TermsGate />
            ) : (
              children
            )}
          </div>
        </main>
      </div>

      {/* Training Video Modal */}
      <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
        <DialogContent className="bg-[#091c3c] border-[#1a3a6a] text-white max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-white">Example Call Recording</DialogTitle>
          </DialogHeader>
          <div className="relative w-full" style={{ paddingTop: "56.25%" }}>
            <iframe
              className="absolute inset-0 w-full h-full rounded-lg"
              src="https://www.youtube.com/embed/LKKbm2E8UZA"
              title="RoofNuts Example Call Recording"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Password Modal */}
      <Dialog open={passwordModalOpen} onOpenChange={setPasswordModalOpen}>
        <DialogContent className="sm:max-w-[400px] bg-[#1e2028] text-white">
          <DialogHeader>
            <DialogTitle className="font-['Chivo'] text-white">Change Password</DialogTitle>
            <DialogDescription className="text-gray-400">
              Enter your current password and choose a new password.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="current_password" className="text-white">Current Password</Label>
              <Input
                id="current_password"
                type="password"
                value={passwordData.current_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                placeholder="Enter current password"
                className="bg-[#2a2d38] border-gray-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="new_password" className="text-white">New Password</Label>
              <Input
                id="new_password"
                type="password"
                value={passwordData.new_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                placeholder="Enter new password"
                className="bg-[#2a2d38] border-gray-600 text-white"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirm_password" className="text-white">Confirm New Password</Label>
              <Input
                id="confirm_password"
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                placeholder="Confirm new password"
                className="bg-[#2a2d38] border-gray-600 text-white"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPasswordModalOpen(false)} className="border-gray-600 text-white hover:bg-gray-700">
              Cancel
            </Button>
            <Button onClick={handlePasswordChange} disabled={changingPassword}>
              {changingPassword ? "Changing..." : "Change Password"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardLayout;
